import { Link, useHistory } from "react-router-dom"
import { Helmet } from "react-helmet"
import classNames from "classnames"
import { useQuery, useMutation } from "@apollo/client"
import makeAsyncScriptLoader from "react-async-script"
import { showAccountProfileModalVar } from "cache"
import { useEffect, useCallback, useState } from "react"
import SuccessToast from "components/shared/SuccessToast"
import portal from "portalConfig"
import isEmpty from "lodash/isEmpty"
import { clearOnLogout } from "apolloClient"
import {
  ACCOUNT_PROFILE_MODAL_TYPE,
  REDIRECT_TO_CONTACT_US_TEXT
} from "shared/constants"
import { generateRedirectUrl } from "shared/helpers/url"
import {
  useLoginState,
  useVerificationRedirectEffect
} from "components/Login/hooks"
import styles from "./Settings.module.scss"
import { Account, Logout } from "./operations"

const { features } = portal.config

const Settings = () => {
  const [isLoggedIn] = useLoginState()
  useVerificationRedirectEffect()
  const history = useHistory()
  const handleBackNav = history.goBack
  const [csInfo, setCsInfo] = useState({})
  const [logoutUser, { client }] = useMutation(Logout)
  const { loading, data } = useQuery(Account)

  const logout = useCallback(() => {
    clearOnLogout(client)
    logoutUser({
      variables: {
        input: {}
      }
    })
  }, [client, logoutUser])

  useEffect(() => {
    if (data && isEmpty(data.me)) logout()
    if (data && data.me) {
      setCsInfo(data.me)
    }
  }, [data, logout])

  useEffect(() => {
    if (!isLoggedIn) {
      history.replace("/")
    }
  }, [isLoggedIn, history])

  return (
    <div className="h-100">
      <Helmet title="Settings" />
      <div className={styles.item}>
        <div
          className={classNames("text-decoration-none", styles.menuLink)}
          onClick={handleBackNav}
        >
          <i className="bi-chevron-left" />
          &nbsp;Back
        </div>
      </div>
      <div className={styles.menuGroupLink}>
        {features.accessCodes && (
          <Link
            to="/parking-codes"
            className={classNames(
              "text-decoration-none",
              "text-center",
              styles.menuLink,
              styles.addMargin
            )}
          >
            View parking codes
          </Link>
        )}
        <Link
          to="/parking-reservations"
          className={classNames(
            "text-decoration-none",
            "text-center",
            styles.menuLink,
            styles.addMargin
          )}
        >
          Manage parking reservations
        </Link>
        <button
          type="button"
          onClick={() => {
            showAccountProfileModalVar(ACCOUNT_PROFILE_MODAL_TYPE.settings)
          }}
          className={classNames(
            "text-decoration-none",
            "text-center",
            styles.supportButton,
            styles.addMargin
          )}
        >
          Profile
        </button>
        {!features.showAdaSupport && (
          <Link
            to={generateRedirectUrl("/support/contact-us", {
              destUrl: `${process.env.REACT_APP_RSVP_BASE}/support/contact-us`,
              originUrl: `${process.env.REACT_APP_RSVP_BASE}/settings`,
              text: REDIRECT_TO_CONTACT_US_TEXT
            })}
            className={classNames(
              "text-decoration-none",
              "text-center",
              styles.menuLink,
              styles.addMargin
            )}
            target="_blank"
            rel="noreferrer"
          >
            Support
          </Link>
        )}
        {features.showAdaSupport && (
          <button
            type="button"
            onClick={() => {
              if (window.adaEmbed) {
                window.adaEmbed.start({
                  handle: "honk",
                  adaReadyCallback: () => {
                    window.adaEmbed.toggle()
                  },
                  toggleCallback: (isDrawerOpen) => {
                    if (!isDrawerOpen) {
                      window.adaEmbed.stop()
                    }
                  },
                  metaFields: csInfo
                })
              }
            }}
            className={classNames(
              "text-decoration-none",
              "text-center",
              styles.supportButton,
              styles.addMargin
            )}
          >
            Support
          </button>
        )}
        <Link
          to="/"
          onClick={logout}
          className={classNames(
            "text-decoration-none",
            "text-center",
            styles.menuLink,
            styles.addMargin
          )}
        >
          Logout
        </Link>
      </div>
      {!loading && (
        <div className="text-muted mt-5 mb-3 text-center">
          Logged in as {data.me && data.me.emailAddress}
        </div>
      )}
      <SuccessToast />
    </div>
  )
}

let Component = Settings

if (features.showAdaSupport) {
  Component = makeAsyncScriptLoader("https://static.ada.support/embed2.js", {
    scriptId: "__ada",
    attributes: {
      "data-handle": "honk",
      "data-lazy": true
    }
  })(Settings)
}

export default Component
